import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowRight, Check } from "lucide-react"
import { PublicNavbar } from "@/components/public-navbar"
import { PricingPlans } from "@/components/pricing-plans"
import { siteUrl } from "@/lib/seo"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { authClient } from "@/lib/auth-client"

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing | intoshort" },
      {
        name: "description",
        content:
          "Try intoshort on one video for free, then compare Basic, Pro and High volume monthly and yearly plans.",
      },
      {
        name: "robots",
        content:
          siteUrl && import.meta.env.PROD ? "index,follow" : "noindex,follow",
      },
      { property: "og:title", content: "Pricing | intoshort" },
      {
        property: "og:description",
        content:
          "Try one source video with access to up to 3 shorts for free. Upgrade to unlock all generated shorts and process more videos.",
      },
      { property: "og:locale", content: "en_US" },
      { property: "og:type", content: "website" },
      ...(siteUrl
        ? [{ property: "og:url", content: `${siteUrl}/pricing` }]
        : []),
    ],
    links: siteUrl ? [{ rel: "canonical", href: `${siteUrl}/pricing` }] : [],
  }),
  component: PricingPage,
})

function PricingPage() {
  const navigate = Route.useNavigate()
  const { data: session } = authClient.useSession()
  return (
    <div className="landing pricing-page">
      <a className="skip-link" href="#pricing-content">
        Skip to content
      </a>
      <PublicNavbar />
      <main id="pricing-content" className="page-width pricing-main">
        <div className="pricing-intro">
          <h1>More shorts from the footage you already have.</h1>
          <p>
            Find the moments worth sharing. Get captions and vertical framing,
            then review the cuts and make them yours before you export.
          </p>
        </div>
        {/* Trial offer: lead with the footage, scan the inclusions, then act.
            Keep the white canvas, ink CTA and all trial conditions visible. */}
        <Alert
          className="pricing-free-offer"
          role="region"
          aria-labelledby="pricing-trial-title"
        >
          <div className="pricing-trial-content">
            <AlertTitle>
              <h2 id="pricing-trial-title">
                Your footage. Your first shorts. Free.
              </h2>
            </AlertTitle>
            <AlertDescription>
              Try intoshort on one source video before choosing a plan.
            </AlertDescription>
            <ul className="pricing-trial-benefits">
              <li>
                <Check aria-hidden="true" />
                Access up to 3 shorts
              </li>
              <li>
                <Check aria-hidden="true" />
                Editing tools included
              </li>
              <li>
                <Check aria-hidden="true" />
                Export without branding
              </li>
            </ul>
          </div>
          <div className="pricing-trial-action">
            <p className="pricing-trial-price">
              $0 <span>· No credit card</span>
            </p>
            <Button
              size="lg"
              render={
                session ? (
                  <Link to="/app" />
                ) : (
                  <Link to="/signup" reloadDocument />
                )
              }
            >
              {session ? "Open intoshort" : "Try one video for free"}
              <ArrowRight data-icon="inline-end" />
            </Button>
          </div>
          <p className="pricing-trial-note">
            One trial per account. Upgrade for more videos and any remaining
            shorts. Free projects and their files are deleted 3 days after
            creation. Results depend on the usable moments in your source.
          </p>
        </Alert>
        <PricingPlans
          reverseOrder
          onChoose={(plan, period) => {
            void navigate({ to: "/app/billing", search: { plan, period } })
          }}
        />
        <section className="pricing-faq" aria-labelledby="pricing-faq-title">
          <div>
            <h2 id="pricing-faq-title">Before you choose.</h2>
            <p>
              A few details about your footage, your allowance and your trial.
            </p>
          </div>
          <div className="pricing-faq-list">
            {pricingQuestions.map(({ question, answer }) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}

const pricingQuestions = [
  {
    question: "What counts as a source video minute?",
    answer:
      "The duration of the video you process, not the length or number of shorts you export. A 60-minute source uses 60 minutes. Editing and exporting its shorts do not count the source again. Deleting a video does not restore its consumed allowance.",
  },
  {
    question: "What can I do with the free trial?",
    answer:
      "Process one source video and access up to the first 3 generated shorts in source order. You can edit and export those shorts without intoshort branding. Any additional shorts stay locked until you upgrade. Free projects, source videos and shorts are deleted 3 days after project creation. Upgrade before expiration to keep a project if it fits in your plan’s cloud storage. The trial is available once per account; deleting the video does not reset it. The number of shorts is not guaranteed.",
  },
  {
    question: "How do I choose between Basic, Pro and High volume?",
    answer:
      "Add up the duration of the source videos you expect to process each month. All paid plans give access to every generated short. Basic includes 50 GB of cloud storage and exports up to 1080p. Pro includes 100 GB and exports up to 4K; High volume includes 150 GB and exports up to 4K. Choose the source minute allowance and storage that cover your footage. High volume is an individual plan, not a team plan.",
  },
  {
    question: "What happens if I run out of minutes?",
    answer:
      "A video that exceeds your remaining allowance cannot be processed. You can choose a larger plan or wait for your allowance to renew. Unused minutes do not roll over. Changing plans does not erase minutes already used.",
  },
  {
    question: "How does yearly billing work?",
    answer:
      "The full annual amount shown on your chosen plan is charged in one payment, in USD. You receive a monthly video allowance, not all of the year's minutes at once. On yearly plans the allowance renews on the first of each month in UTC. Monthly plans follow their monthly billing period.",
  },
  {
    question: "How long are my videos kept?",
    answer:
      "Free trial projects are deleted 3 days after creation, including source videos and shorts. Paid projects are kept within your cloud storage allowance: 50 GB for Basic, 100 GB for Pro and 150 GB for High volume. Projects that do not fit are temporary and are deleted 3 days after creation. Free up enough space or upgrade before expiration to keep them. Download any exports you want to retain before their project expires.",
  },
]
